import { useEffect } from 'react';
import { callDeepLink } from '@croquiscom-pvt/web2app';
import { useRouter } from 'next/router';

import { useIsZigzag } from '@common/device-manager';
import { useOnceReveal } from '@common/hooks';
import { useLandingPopup } from '@common/overlay-manager';
import { gotoLogin } from '@common/utils';
import { useAuthenticationState } from '@widgets/authentication';
import { SellerBadge, ShopInfo, ShopUxUbl, useStoreHomeUBL } from '@domains/storehome-common';

interface UseShopInformationServiceProps {
  shop_info: ShopInfo;
}

interface CouponDownloadClickProps {
  link?: string;
  ubl?: ShopUxUbl | null;
}

// !NOTE: 법무 이슈 긴급 대응 - https://croquis.slack.com/archives/C02CSEVTF1A/p1756363464087989
const ZARA_SHOP_ID = '35993';
const UNIQLO_SHOP_ID = '49233';

const BUYING_SERVICE_SHOP_ID_LIST = [ZARA_SHOP_ID, UNIQLO_SHOP_ID];

export const useShopInformationService = ({
  shop_info: { id, age_list, style_list, seller_badge, main_domain, representative_coupon_v2, info_landing_url },
}: UseShopInformationServiceProps) => {
  const router = useRouter();
  const {
    couponDownloadClickUBL,
    shopNameMoreButtonClickUBL,
    shopInfoButtonClickUBL,
    impressionSellerGradeUBL,
    couponImressionUBL,
  } = useStoreHomeUBL(id);
  const { open } = useLandingPopup();
  const is_zigzag = useIsZigzag();
  const { authState } = useAuthenticationState();

  const isLoggedIn = authState === 'authenticated';

  const style_value_list = style_list.map((style) => style.value);
  const description = [...age_list, ...style_value_list];
  const has_info_url = !!info_landing_url;

  // TODO: 서버에서 구매대행 뱃지 대응되면 하드코딩 제거
  const buying_service_badge = BUYING_SERVICE_SHOP_ID_LIST.includes(id) ? ['구매대행'] : [];
  const seller_badges = seller_badge?.filter((badge): badge is SellerBadge => badge !== null) ?? [];

  const { targetRef: coupon_ref, isRevealed: is_coupon_revealed } = useOnceReveal();

  useEffect(() => {
    if (!is_coupon_revealed) {
      couponImressionUBL(representative_coupon_v2?.ubl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_coupon_revealed]);

  const createCouponDownloadUrl = (link?: string) => {
    if (link) {
      return link;
    }

    return `/coupon/box/${id}`;
  };

  const redirectToLogin = () => {
    if (is_zigzag) {
      callDeepLink('/login')();
      return;
    }

    gotoLogin({ redirect_url: createCouponDownloadUrl() });
  };

  const handleBookmarkClick = async () => {
    open();
  };

  const handleShopNameInfoButtonClick = () => {
    shopNameMoreButtonClickUBL();
    if (has_info_url) {
      router.push(`/${main_domain}/information`);
    }
  };

  const handleInfoButtonClick = () => {
    shopInfoButtonClickUBL();
    if (has_info_url) {
      router.push(`/${main_domain}/information`);
    }
  };

  const handleBadgeImpression = (ubl?: ShopUxUbl | null) => {
    impressionSellerGradeUBL(ubl);
  };

  const handleCouponDownloadClick = (params?: CouponDownloadClickProps) => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }

    const { link, ubl } = params ?? {};

    couponDownloadClickUBL(ubl);
    window.open(createCouponDownloadUrl(link), '_blank');
  };

  return {
    coupon_ref,
    description,
    buying_service_badge,
    seller_badges,
    has_info_url,
    handleBookmarkClick,
    handleCouponDownloadClick,
    handleInfoButtonClick,
    handleShopNameInfoButtonClick,
    handleBadgeImpression,
  };
};
